<template>
  <div class="flight-block">
    <h3>{{ flightTitle }}</h3>
    <div class="route">{{ route }}</div>
    <div class="table-flights">
      <div class="table-header">
        <div
          v-if="!isMobile"
          class="first-col"
        >
          <dropdown
            :items="sorterItems"
            :device-type="deviceType"
            :mobile-title="''"
            :selected-item="selectedSorter"
            :selected-title="null"
            @select="sortTable"
          />
        </div>
        <span
          :class="['navigation', 'prev', {'disabled': prevDateDisabled }]"
          @click="prevDay('departure')"
        />

        <div
          v-if="data"
          class="days-col"
        >
          <div
            v-for="(item, index) in headerData"
            :key="index"
            :class="['cell', { 'disabled': !item.enabled }]"
          >
            <div>
              {{ item.day }}
              <span class="date">{{ item.date }}</span>
            </div>
          </div>
        </div>
        <span
          class="navigation next"
          @click="nextDay('departure')"
        />
      </div>

      <div
        v-for="(row, rowIndex) in tableData"
        :key="rowIndex"
        :class="['flight-row', { 'selected': row.selected }]"
      >
        <div class="a220-image" />
        <div
          class="first-col flight-details"
          @click="clickTile(rowIndex)"
        >
          <div class="flight-details-wrap">
            <div class="departure-details">
              <span class="time">{{ row.departTime }}</span>
              <span class="airport">{{ row.orig }}</span>
            </div>
            <div class="duration-details">
              <span class="duration">{{ row.duration }}</span>
              <span
                v-if="row.conn"
                class="flight-type connection"
              >
                {{ row.conn }}
              </span>
              <span
                v-else
                class="flight-type"
              >
                {{ $t('timetable.direct') }}
              </span>
            </div>
            <div class="arrive-details">
              <span class="time">{{ row.arrivalTime }}</span>
              <span class="airport">{{ row.dest }}</span>
              <span
                v-if="row.nextDay"
                class="nextDay"
              >+1</span>
            </div>
          </div>
        </div>
        <div
          v-if="!isMobile"
          class="days-col"
        >
          <div
            v-for="(cell, cellIndex) in row.dates"
            :key="cellIndex"
            :class="['cell', { 'disabled': isCellDisabled(cell, cellIndex), 'selected': false }]"
          >
            <radio-item
              v-if="cell.hasFlight"
              tabindex="0"
              class="no-label"
              :name="flightType"
              :value="''"
              :label="''"
              :disabled="!headerData[`${cellIndex}`].enabled"
              :is-checked="cell.selected"
              @change="selectFlight(rowIndex, cellIndex)"
            />
          </div>
        </div>
      </div>

      <div
        v-if="!tableData || !tableData.length"
        class="flight-row"
      >
        <div
          v-if="!isMobile"
          class="first-col flight-details"
        />
        <div class="days-col empty">
          <span>{{ noDataText }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import moment from 'moment'
  import cloneDeep from 'lodash-es/cloneDeep'
  import isEmpty from 'lodash-es/isEmpty'
  import RadioItem from 'components/Radio/RadioItem'
  import Dropdown from 'components/Dropdown/DropdownSimple'

  export default {
    components: {
      RadioItem,
      Dropdown
    },

    props: {
      flightTitle: {
        type: String,
        default: ''
      },

      route: {
        type: String,
        default: ''
      },

      flightType: {
        type: String,
        default: ''
      },

      data: {
        type: Array,
        default: () => {}
      },
      minDate: {
        type: String,
        default: ''
      },
      flightDay: {
        type: [Date, String, Object],
        default: ''
      },
      selectedSorter: {
        type: String,
        default: ''
      },
      selectedDate: {
        type: [Object, Date],
        default: () => {}
      },
      noDataText: {
        type: String,
        default: ''
      }
    },

    data () {
      return {
        tableData: [],
        sorterItems: [
          {
            value: 'departure',
            title: this.$t('reusable.sorters.departureTime')
          },
          {
            value: 'arrival',
            title: this.$t('reusable.sorters.arrivalTime')
          },
          {
            value: 'duration',
            title: this.$t('reusable.sorters.flightDuration')
          }
        ]
      }
    },
    computed: {
      headerData () {
        const flight = moment(this.flightDay)
        const header = this.createHeaderDatesArray(flight)
        return this.setEnabledHeaderDates(this.data, header)
      },

      dayCount () {
        return this.isMobile ? 1 : 7
      },

      flightTypeCapitalized () {
        return this.flightType.charAt(0).toUpperCase() + this.flightType.slice(1)
      },

      prevDateDisabled () {
        const newDate = moment(this.flightDay, 'DD.MM.YYYY').add(-1, 'days').format('DD.MM.YYYY')
        return moment(this.minDate, 'DD.MM.YYYY').isAfter(moment(newDate, 'DD.MM.YYYY'))
      },

      ...mapState('device', [
        'deviceType'
      ]),
      ...mapGetters('device', [
        'isMobile',
        'isDesktop'
      ])
    },
    methods: {
      selectFlight (rowIndex, cellIndex) {
        const data = this.clearSelection(this.tableData)

        data[`${rowIndex}`].selected = true
        data[`${rowIndex}`].dates[`${cellIndex}`].selected = true

        const selectedDate = cloneDeep(data[`${rowIndex}`])
        selectedDate.date = this.headerData[`${cellIndex}`].fullDate
        if (this.flightType === 'return') {
          this.$store.commit(`timetable/setSelectedReturnDate`, selectedDate)
          this.$store.commit(`timetable/setReturnData`, data)
        } else {
          this.$store.commit(`timetable/setSelectedDepartureDate`, selectedDate)
          this.$store.commit(`timetable/setDepartureData`, data)

          const returnDate = this.$store.state.timetable.selectedReturn.date
          if (returnDate && moment(selectedDate.date, 'DD.MM.YYYY').isAfter(moment(returnDate, 'DD.MM.YYYY'))) {
            const returnData = this.clearSelection(this.$store.state.timetable.returnData)
            this.$store.commit(`timetable/setSelectedReturnDate`, {})
            this.$store.commit(`timetable/setReturnData`, returnData)
          }
        }
      },

      isDirectFlight (flight) {
        return isEmpty(flight.conn)
      },

      clearSelection (data) {
        const clearedData = cloneDeep(data)
        clearedData.forEach((row) => {
          row.selected = false
          row.dates.forEach((c) => {
            c.selected = false
          })
        })
        return clearedData
      },

      clickTile (rowIndex) {
        if (this.isMobile && this.headerData[0].enabled) {
          this.selectFlight(rowIndex, 0)
        }
      },

      sortTable (item) {
        if (this.flightType === 'departure') {
          this.$store.commit(`timetable/setDepartureSorter`, item)
        } else {
          this.$store.commit(`timetable/setReturnSorter`, item)
        }
      },

      prevDay () {
        if (this.prevDateDisabled) {
          return
        }
        const newDate = moment(this.flightDay).subtract(1, 'days')

        let key = 'updateReturnDate'
        let flight = 'setSelectedReturnDate'
        let dir = 'setReturnData'
        if (this.flightType === 'departure') {
          key = 'updateDepartureDate'
          flight = 'setSelectedDepartureDate'
          dir = 'setDepartureData'
        }

        this.$store.dispatch(`eHorizonSearchForm/calendar/${key}`, newDate, { root: true })
        const data = this.clearSelection(this.tableData)
        this.$store.commit(`timetable/${dir}`, data)
        this.$store.commit(`timetable/${flight}`, {})

        this.$emit('getTimetable')
      },

      nextDay () {
        const newDate = moment(this.flightDay).add(1, 'days')
        let key = 'updateReturnDate'
        let flight = 'setSelectedReturnDate'
        let dir = 'setReturnData'
        if (this.flightType === 'departure') {
          key = 'updateDepartureDate'
          flight = 'setSelectedDepartureDate'
          dir = 'setDepartureData'
        }

        this.$store.dispatch(`eHorizonSearchForm/calendar/${key}`, newDate, { root: true })
        const data = this.clearSelection(this.tableData)
        this.$store.commit(`timetable/${dir}`, data)
        this.$store.commit(`timetable/${flight}`, {})

        this.$emit('getTimetable')
      },

      createHeaderDatesArray (initDate) {
        const header = []
        let firstDay = null

        if (!this.isDesktop) {
          firstDay = initDate
        } else {
          firstDay = moment(initDate).add(-3, 'days').format('DD.MM.YYYY')
        }

        for (let i = 0; i < this.dayCount; i++) {
          const date = moment(firstDay, 'DD.MM.YYYY').add(i, 'days')
          header.push({
            fullDate: date.format('DD.MM.YYYY'),
            day: date.format('ddd'),
            date: date.format('MMM DD')
          })
        }

        return header
      },

      setEnabledHeaderDates (dataArray, header) {
        this.tableData = dataArray
        const data = cloneDeep(dataArray)

        data.forEach((row) => {
          row.dates.forEach((cell, index) => {
            if (cell && header[`${index}`]) {
              const date = header[`${index}`].fullDate
              if (!moment(this.minDate, 'DD.MM.YYYY').isAfter(moment(date, 'DD.MM.YYYY')) && cell.hasFlight) {
                header[`${index}`].enabled = true
              }

              if (header[`${index}`].fullDate === this.selectedDate.date &&
                row.departTime === this.selectedDate.departTime &&
                row.arrivalTime === this.selectedDate.arrivalTime) {
                row.selected = true
                cell.selected = true
              }
            }
          })
        })

        return header
      },

      isCellDisabled (cell, cellIndex) {
        return !this.headerData[`${cellIndex}`].enabled || !cell.hasFlight
      }
    }
  }
</script>


<style lang="scss" scoped rel="stylesheet/scss">
  @import "~assets/styles/common.scss";

  .flight-block {
    padding: 28px 0 36px;

    @include bp-tablet-only {
      padding: 40px 0;
    }

    @include bp-mf-desktop {
      padding: 44px 0 56px;
    }

    &.return {
      border-top: 1px solid $timetable-gray-border;
    }

    h3 {
      margin: 0 0 9px;
      font-size: em(16);
      line-height: 19px;
      font-weight: 500;

      @include bp-mf-tablet {
        margin-bottom: 17px;
        font-size: em(24);
        line-height: 28px;
      }
    }

    .route {
      font-size: em(16);
      line-height: 19px;

      @include bp-mf-tablet {
        font-size: em(20);
        line-height: 24px;
      }
    }
  }

  .table-flights {
    margin-top: 18px;

    @include bp-tablet-only {
      margin-top: 30px;
    }

    @include bp-mf-desktop {
      margin-top: 46px;
    }
  }

  .navigation {
    display: block;
    position: absolute;
    top: calc(50% - 16px);
    z-index: 0;
    width: 32px;
    height: 32px;
    background-color: #ffffff;
    border: 1px solid $formInputBorder;
    border-radius: 50%;
    cursor: pointer;

    @media print {
      display: none;
    }

    &:hover:before {
      color: $blue-hover;
    }

    &:before {
      content: $icon-arrow-right-thin-medium;
      @include icon-font-thin();
      @include center-block();
      font-size: em(11);
      line-height: 32px;
      font-weight: 700;
      color: $blue-medium;
    }

    &.disabled,
    &.disabled:hover {

      &:before {
        color: $grey;
        cursor: default;
      }
    }

    &.prev {
      left: 0;

      @include bp-tablet-only {
        left: 355px;
      }

      @include bp-mf-desktop {
        left: calc(50% - 150px)
      }

      &:before {
        transform: rotate(-180deg);
        right: 11px;
      }
    }

    &.next {
      right: 0;

      &:before {
        left: 11px;
      }
    }
  }

  .a220-image {
    display: block;
    height: 100%;
    width: 13px;
    background: url("~assets/images/airbus-a220_300.svg") no-repeat;
    background-size: cover;

    @include bp-mf-tablet {
      width: 68px;
    }

    @include bp-mf-desktop {
      width: 17px;
    }

    @include bp-mf-desktop-large {
      width: 15px;
    }
  }

  .table-header,
  .flight-row {
    display: flex;
    position: relative;
    justify-content: space-between;
  }

  .flight-row {
    height: 80px;
    background-color: #ffffff;
    border: 1px solid $gray-medium-brown;
    border-radius: 5px 4px 4px 5px;
    box-shadow: 0 2px 4px 0 rgba(211, 211, 211, 0.5);

    @include bp-mf-tablet {
      padding: 0 47px 0 0;
      border-radius: 7px 4px 4px 7px;
      height: 91px;
    }

    @include bp-mf-desktop {
      padding: 0 59px 0 0;
    }

    @media print {
      padding: 0;
      box-shadow: none;
    }

    &:not(:last-child) {
      margin-bottom: 12px;
    }

    &.selected {
      padding-left: 0;
    }

    .cell:not(.disabled) {
      padding-bottom: 3px;
      cursor: pointer;

      &.selected {
        padding-bottom: 0;
        background-color: #ecedf4;
        border-bottom: 3px solid $blue-medium;
      }

      &:hover {
        background-color: #ecedf4;
      }
    }
  }

  .first-col {
    text-align: center;

    @include bp-tablet-only {
      width: 392px;
      flex: none;
    }

    @include bp-mf-desktop {
      width: 439px;
    }

    @media print {
      width: 300px!important;
      flex: none;
    }

    &.flight-details {
      padding: 0 16px;
      margin: 0 auto;

      @media print {
        padding: 0;
      }

      @include bp-mf-tablet {
        padding: 0;
        margin: 0;
      }
    }
  }

  .flight-details-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;

    span {
      display: block;
    }

    .time {
      font-size: em(16);
      line-height: 19px;
      font-weight: 500;

      @include bp-mf-tablet {
        font-size: em(20);
        line-height: 24px;
      }
    }

    .airport {
      margin-top: 16px;
      font-size: em(12);
      line-height: 14px;
      font-weight: 500;
      color: $grey;

      @include bp-mf-tablet {
        margin-top: 13px;
        font-size: em(14);
        line-height: 16px;
      }
    }

    .flight-type {
      position: relative;
      color: $grey;

      &:before {
        content: '';
        display: block;
        margin: 13px 0;
        width: 100%;
        height: 1px;
        background-color: #cfcfcf;
      }

      &.connection:after {
        content: '';
        display: block;
        position: absolute;
        top: -6px;
        right: 0;
        left: 0;
        margin: 0 auto;
        padding: 3px;
        width: 4px;
        height: 4px;
        background-color: $grey-default;
        border: 4px solid #ffffff;
        border-radius: 50%;
      }
    }
  }

  .departure-details,
  .duration-details,
  .arrive-details {
    display: inline-block;
    vertical-align: middle;
  }

  .departure-details {
    text-align: left;
  }

  .duration-details {
    margin: 0 24px;
    width: 66px;
    font-size: em(12);
    line-height: 14px;

    @include bp-mf-tablet {
      width: 142px;
    }

    @media print {
      width: 102px;
      margin: 0 20px;
    }
  }

  .arrive-details {
    text-align: right;
    position: relative;

    .nextDay {
      color: #88BCF9;
      font-size: em(14);
      line-height: 19px;
      position: absolute;
      right: -30px;
      top: calc(50% - 9px);
    }
  }

  .days-col {
    display: flex;
    border-left: 1px solid $gray-medium-brown;
    border-right: 1px solid $gray-medium-brown;
    overflow: hidden;

    &.empty {
      padding: 0 16px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      color: $grey;
      text-align: center;
      width: 100%;
    }

    @include bp-tablet-only {
      width: 1250px;
    }

    .cell {
      height: auto;
      align-self: center;

      @include bp-mf-tablet {
        height: 100%;
      }
    }
  }

  .cell {
    height: 100%;
    width: 100%;
    text-align: center;
    line-height: 46px;

    @include bp-mf-tablet {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 86px;
      line-height: 15px;
    }

    @include bp-tablet-only {
      flex: none;
    }

    @media print {
      width: 68px!important;
      border-right: 1px solid $gray-medium-brown!important;
    }

    &:not(:last-child) {

      @include bp-mf-tablet {
        border-right: 1px solid $gray-medium-brown;
      }
    }

    ::v-deep .radio-wrap.no-label {
      width: auto;
      height: auto;
    }
  }

  .date {

    @include bp-mf-tablet {
      display: block;
    }
  }

  .table-header {
    padding: 0 65px;
    height: 48px;
    border: 1px solid transparent;

    @include bp-mf-tablet {
      padding: 0 47px 0 0;
      height: 65px;
    }

    @include bp-mf-desktop {
      padding: 0 59px 0 0;
    }

    @media print {
      padding: 0;
    }

    .first-col {
      width: 406px;
      text-align: left;

      @include bp-mf-desktop {
        width: 456px;
      }
    }

    .cell {
      line-height: 16px;
      font-weight: 500;

      @include bp-mf-tablet {
        font-size: em(12);
        line-height: 14px;
      }

      &.disabled {
        color: $grey;
      }
    }

    .days-col {
      border: none;
      width: 100%;

      @include bp-mf-tablet {
        border: 1px solid $gray-medium-brown;
        border-bottom: none;
        width: auto;
      }

      .date {
        display: inline-block;
        margin-left: 3px;

        @include bp-mf-tablet {
          margin-left: 0;
          display: block;
        }

        &:before {
          content: ',';
          position: relative;
          left: -3px;

          @include bp-mf-tablet {
            display: none;
          }

          @media print {
            display: none;
          }
        }
      }
    }
  }

  ::v-deep .dropdown-wrap {
    width: auto;
    min-width: 160px;
    text-align: left;

    .dropdown-field {
      border-radius: 4px;
    }

    .dropdown-list {
      height: auto;
    }
  }
</style>
